import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type { Person, PersonWithDetails, PersonSearchParams } from '../types/person.js';
import * as personApi from './person-api.js';
import { wsClient } from './websocket-client.js';

export type PersonLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

let sharedPeople: Person[] = [];
let sharedActivePerson: PersonWithDetails | null = null;
let sharedLoadingState: PersonLoadingState = 'idle';
let sharedError: string | null = null;
let sharedSearchParams: PersonSearchParams = {};

const hosts = new Set<ReactiveControllerHost>();

function notifyHosts(): void {
  for (const host of hosts) {
    host.requestUpdate();
  }
}

async function loadPeople(params: PersonSearchParams = {}): Promise<void> {
  if (sharedLoadingState === 'loading') return;
  sharedLoadingState = 'loading';
  sharedError = null;
  sharedSearchParams = params;
  notifyHosts();

  try {
    sharedPeople = await personApi.getPeople(params);
    sharedLoadingState = 'loaded';
  } catch (e) {
    sharedLoadingState = 'error';
    sharedError = e instanceof Error ? e.message : 'Personen konnten nicht geladen werden';
  }

  notifyHosts();
}

function onWsMessage(e: Event): void {
  const detail = (e as CustomEvent).detail as { type: string };
  const type = detail.type;

  if (type === 'person:created' || type === 'person:updated' || type === 'person:deleted') {
    void loadPeople(sharedSearchParams);
  }
}

wsClient.addEventListener('ws-message', onWsMessage);

export class PersonStateController implements ReactiveController {
  private host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    hosts.add(this.host);
  }

  hostDisconnected(): void {
    hosts.delete(this.host);
  }

  get people(): Person[] {
    return sharedPeople;
  }

  get activePerson(): PersonWithDetails | null {
    return sharedActivePerson;
  }

  get loadingState(): PersonLoadingState {
    return sharedLoadingState;
  }

  get error(): string | null {
    return sharedError;
  }

  async loadAll(params?: PersonSearchParams): Promise<void> {
    await loadPeople(params);
  }

  async loadOne(id: number): Promise<void> {
    try {
      sharedActivePerson = await personApi.getPerson(id);
      notifyHosts();
    } catch (e) {
      sharedError = e instanceof Error ? e.message : 'Person konnte nicht geladen werden';
      notifyHosts();
    }
  }

  async refresh(): Promise<void> {
    await loadPeople(sharedSearchParams);
  }
}
